<template>
  <div class="faculty-card" :class="{ 'faculty-card--pointer': readonly }" @click="$emit('click')">
    <div class="faculty-card__photo">
      <div v-if="!preloader && !faculty.photo" class="faculty-card__missing-photo">
        <UserIcon />
      </div>
      <img v-else-if="!preloader" :src="faculty.photo" alt="" />
      <div v-else class="ajax__line" />
    </div>
    <div v-if="!preloader" class="faculty-card__title">
      <div class="faculty-card__coach-list">
        <div v-for="(coach, index) in faculty.trainers" :key="index" class="faculty-card__coach">
          <div :key="'name_' + coach.id" class="faculty-card__coach-name">
            {{ coach.name }}
          </div>
          <span>
            <a
              v-if="coach.vk"
              :key="'vk_' + coach.id"
              :href="'https://vk.com/' + coach.vk"
              target="_blank"
              class="faculty-card__coach-social"
            >
              <VkCoachIcon />
            </a>
            <a
              v-if="coach.telegram"
              :key="'tg_' + coach.id"
              :href="'https://t.me/' + coach.telegram"
              target="_blank"
              class="faculty-card__coach-social"
            >
              <TelegramCoachIcon />
            </a>
          </span>
        </div>
      </div>
      <div class="faculty-card__faculty-name">
        {{ faculty.trainers.length === 1 ? 'Тренер' : 'Тренеры' }}
        сборной : &laquo;{{ faculty.name }}&raquo;
      </div>
    </div>
    <div v-else class="faculty-card__title">
      <div class="faculty-card__coach-name ajax__line -round-" />
      <div class="faculty-card__faculty-name ajax__line -round-" />
    </div>
    <div v-if="!preloader" class="faculty-card__achievement-list">
      <ul>
        <li
          v-for="achievement in achievements"
          :key="achievement"
          class="faculty-card__achievement"
        >
          <span>{{ achievement }}</span>
        </li>
      </ul>
    </div>
    <div v-else class="faculty-card__achievement-list">
      <div class="faculty-card__achievement ajax__line -round-" />
      <div class="faculty-card__achievement ajax__line -round-" />
      <div class="faculty-card__achievement ajax__line -round-" />
    </div>
    <div v-if="!preloader" class="faculty-card__controls">
      <Button
        v-if="readonly && facultyChangeCount === 0 && isUserFaculty"
        class="faculty-card__exit"
        variant="primary"
        size="s"
        @click.stop="onChangeButtonClick"
      >
        Выйти из сборной
      </Button>
      <template v-if="!readonly">
        <div class="faculty-card__icon faculty-card__edit" @click="$emit('edit', faculty.id)">
          <EditIcon />
        </div>
        <div class="faculty-card__icon faculty-card__delete" @click="$emit('delete', faculty.id)">
          <DeleteIcon />
        </div>
        <Button
          class="faculty-card__control-btn faculty-card__control-btn--delete"
          :variant="'outline'"
          :size="'s'"
          @click="$emit('delete', faculty.id)"
        >
          Удалить сборную
        </Button>
        <Button
          class="faculty-card__control-btn faculty-card__control-btn--edit"
          :variant="'primary'"
          :size="'s'"
          @click="$emit('edit', faculty.id)"
        >
          Редактировать сборную
        </Button>
      </template>
      <div
        class="faculty-card__niche-icon"
        :style="{ backgroundColor: '#' + faculty.category.background }"
      >
        <img v-if="faculty.category.icon" :src="faculty.category.icon" alt="" />
      </div>
    </div>
  </div>
</template>
<script>
import Button from '@/components/UiKit/v2/Button';
import EditIcon from '@/components/Icons/EditIcon';
import DeleteIcon from '@/components/Icons/DeleteIcon';
import UserIcon from '@/components/Icons/UserIcon';
import declinationOfNum from '@/functions/declinationOfNum';
import VkCoachIcon from '@/components/Icons/VkCoachIcon';
import TelegramCoachIcon from '@/components/Icons/TelegramCoachIcon';
import { mapState } from 'vuex';

export default {
  name: 'FacultyCard',
  components: {
    EditIcon,
    DeleteIcon,
    VkCoachIcon,
    UserIcon,
    TelegramCoachIcon,
    Button
  },
  props: {
    faculty: {
      type: Object,
      default: undefined
    },
    preloader: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    },
    isUserFaculty: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ...mapState('auth', {
      facultyChangeCount: state => state.data.user.facultyChangeCount
    }),
    achievements() {
      return this.faculty.trainers.length
        ? this.faculty.trainers
            .map(t => t.achievements)
            .reduce((prev, curr) => [...prev, ...curr], [])
        : [];
    }
  },
  methods: {
    declinationOfNum: declinationOfNum,
    onChangeButtonClick() {
      this.$emit('changeFaculty');
    }
  }
};
</script>

<style lang="scss" scoped>
@import 'src/style/utils';

.faculty-card {
  @include uiFont;
  $screen-sm: 800px;

  padding: 20px;
  background-color: #fff;
  border-radius: 8px;
  display: grid;
  grid-template-areas:
    'photo title controls'
    'photo achievements achievements';
  grid-template-columns: 140px 1fr auto;
  grid-template-rows: auto 1fr;
  transition: all 0.3s ease;

  @include media($screen-sm) {
    padding: 20px 16px;
    grid-template-areas:
      'photo title title'
      'achievements achievements achievements'
      'controls controls controls';
    grid-template-columns: 88px auto auto;
    grid-template-rows: auto auto 1fr;
  }

  &--pointer {
    cursor: pointer;
  }

  &__photo {
    grid-area: photo;

    img,
    div {
      width: 140px;
      height: 200px;
      border-radius: 8px;
      object-fit: cover;

      @include media($screen-sm) {
        width: 88px;
        height: 136px;
      }
    }
  }

  &__missing-photo {
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: $ui-background;
  }

  &__title {
    grid-area: title;
    padding-left: 20px;
    padding-right: 8px;

    @include media($screen-sm) {
      padding-left: 16px;
      padding-right: 0;
    }
  }

  &__coach-list {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
  }

  &__coach {
    display: flex;
    margin-right: 16px;
    align-items: center;
    margin-bottom: 8px;

    &:last-child {
      margin-right: 0;
    }

    @include media($screen-sm) {
      width: 100%;
      justify-content: space-between;
      margin-right: 0;
    }

    &-social,
    &-name {
      margin-right: 8px;

      &:last-child {
        margin-right: 0;
      }
    }

    &-name {
      font-size: 21px;
      line-height: 24px;
      font-weight: 700;
      word-break: break-word;

      @include media($screen-sm) {
        font-weight: 500;
        font-size: 18px;
        line-height: 24px;
      }

      &.ajax__line {
        width: 100%;
        height: 24px;
      }
    }

    span,
    span a {
      display: flex;
      align-items: center;
    }
  }

  &__faculty-name {
    font-weight: 500;
    font-size: 14px;
    line-height: 20px;
    color: $ui-light-grey-4-5-contrast;

    &.ajax__line {
      width: 50%;
      height: 20px;
      margin-top: 8px;
    }
  }

  &__achievement-list {
    grid-area: achievements;
    padding-left: 36px;

    @include media($screen-sm) {
      padding-left: 0;
      margin-bottom: 20px;
    }

    ul {
      list-style-type: disc;

      @include media($screen-sm) {
        padding-left: 16px;
      }
    }
  }

  &__achievement {
    margin-top: 12px;
    font-weight: 400;
    font-size: 16px;
    line-height: 20px;

    &.ajax__line {
      width: 60%;
      height: 20px;
      margin-bottom: 8px;
      margin-left: -16px;
    }

    span {
      position: relative;
      left: -5px;
    }

    @include media($screen-sm) {
      font-size: 14px;
    }
  }

  &__controls {
    grid-area: controls;
    display: flex;
    justify-content: flex-end;
    @include media($screen-sm) {
      flex-direction: column-reverse;
    }
  }

  &__exit {
    margin-right: 20px;

    @include media($screen-sm) {
      margin-right: 0;
      width: 100%;
      margin-top: 16px;
    }
  }

  &__control-btn {
    display: none;
    margin-bottom: 12px;

    &:last-child {
      margin-bottom: 0;
    }

    @include media($screen-sm) {
      display: block;
    }

    &--delete.ui-kit-v2-button {
      color: $ui-red;
      outline: 1px solid $ui-red;

      &:hover:not([disabled]) {
        background-color: $ui-red;
      }
    }

    &--edit {
      margin-top: 16px;
    }
  }

  &__icon {
    cursor: pointer;

    @include media($screen-sm) {
      display: none;
    }

    & svg {
      path {
        fill: #818181;
      }
    }
  }

  &__edit {
    margin-right: 12px;
  }

  &__delete {
    margin-right: 24px;
  }

  &__niche-icon {
    height: 48px;
    width: 48px;
    border-radius: 4px;
    display: flex;
    justify-content: center;
    align-items: center;

    @include media($screen-sm) {
      height: 24px;
      width: 24px;
    }

    img {
      height: 24px;
      width: 24px;

      @include media($screen-sm) {
        height: 16px;
        width: 16px;
      }
    }
  }

  &:hover {
    box-shadow: 0px 4px 10px rgba(43, 45, 52, 0.12);
  }
}
</style>
